import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Description,
  Schedule,
  Sync,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import EscritorioSelect from '../../selects/EscritorioSelect';
import { importService } from '../../../services/importService';

interface ImportClientesModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedData {
  id: string;
  valid: boolean;
  duplicate: boolean;
  validationErrors?: string[];
  importStatus?: 'pending' | 'importing' | 'success' | 'error';
  importError?: string;
  [key: string]: any;
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
}

interface ImportProgress {
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
}

const steps = [
  'Configuração da API',
  'Análise e Importação'
];

export const ImportClientesModal: React.FC<ImportClientesModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiTypeClient, setApiTypeClient] = useState('listarClientesAtivos');
  const [apiUrl, setApiUrl] = useState('https://www.upnetisp.com.br/admin/addons/sysup/apiv2');
  const [apiToken, setApiToken] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');
  const [escritorioId, setEscritorioId] = useState<number | null>(null);
  const [parsedData, setParsedData] = useState<ParsedData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0
  });
  // Estado de edição removido - interface simplificada
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ [key: number]: ImportProgress }>({});
  const [importCompleted, setImportCompleted] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Funções de seleção de checkbox
  const handleSelectRow = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === parsedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(parsedData.map((_, index) => index)));
    }
  };

  const getFieldValidationErrors = (row: ParsedData): { [key: string]: boolean } => {
    const fieldErrors: { [key: string]: boolean } = {};
    
    // Verificar quais campos específicos têm erros
    if (!row.nome) fieldErrors.nome = true;
    if (!row.cpf_cnpj) fieldErrors.cpf_cnpj = true;
    if (!row.login) fieldErrors.login = true;
    if (!row.endereco) fieldErrors.endereco = true;
    if (!row.bairro) fieldErrors.bairro = true;
    if (!row.cidade) fieldErrors.cidade = true;
    if (!row.estado) fieldErrors.estado = true;
    if (!row.cep) fieldErrors.cep = true;
    if (row.email && !row.email.includes('@')) fieldErrors.email = true;
    return fieldErrors;
  };

  const cleanPhoneNumber = (phone: string): string => {
    if (!phone || phone.trim() === '') return '';
    
    // Remover todos os caracteres que não são números
    return phone.replace(/\D/g, '');
  };

  // Função para validar formato de data yyyy-mm-dd
  const isValidDateFormat = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) {
      return false;
    }
    
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  };

  const parseApiData = (clientsData: any[]): ParsedData[] => {
    try {
      const data: ParsedData[] = [];
      
      clientsData.forEach((cliente) => {
        const clienteData: ParsedData = {
          //DADOS DO CLIENTE
          id: cliente.id,
          nome: cliente.nome,
          tipo_pessoa: cliente.tipo_pessoa, // Manter pf/pj como vem da API
          tipo_cliente: cliente.tipo_cliente,
          cpf_cnpj: cliente.cpf_cnpj, // Campo corrigido
          rg_ie: cliente.rg_ie || null, // RG para qualquer tipo de pessoa
          data_nascimento: cliente.data_nascimento || null,
          nome_pai: cliente.nome_pai || null,
          nome_mae: cliente.nome_mae || null,
          endereco_res: cliente.endereco_res || null, // Endereço residencial
          numero_res: cliente.numero_res || null,
          bairro_res: cliente.bairro_res || null, // Bairro residencial
          cidade_res: cliente.cidade_res || null, // Cidade residencial
          estado_res: cliente.estado_res || null, // Estado residencial
          cidade_ibge: cliente.cidade_ibge || null, // Cidade IBGE residencial
          cep_res: cliente.cep_res || null, // CEP residencial
          complemento_res: cliente.complemento_res || null,
          observacao: cliente.observacao || null,
          data_cadastro: cliente.data_cadastro || null,
          status_cliente: cliente.status_cliente,
          //DADOS DO CONTATO DO CLIENTE
          email: cliente.email || '',
          telefone: cleanPhoneNumber(cliente.telefone || null),
          celular1: cleanPhoneNumber(cliente.celular1 || null),
          celular2: cleanPhoneNumber(cliente.celular2 || null),
          recebe_notificacao_sms: cliente.recebe_notificacao_sms,
          recebe_notificacao_email: cliente.recebe_notificacao_email,
          //DADOS DO CONTRATO DO CLIENTE
          contrato_titulo: cliente.contrato_titulo || cliente.nome,
          status_contrato: cliente.status_contrato,
          usuario_pppoe: cliente.usuario_pppoe, // Login PPPoE
          senha_pppoe: cliente.senha_pppoe, // Senha PPPoE
          endereco_mac: cliente.endereco_mac || null,
          endereco_ip: cliente.endereco_ip || null,
          plano: cliente.plano || null,
          simultaneo: cliente.simultaneo, // Boolean para string
          plano15: cliente.plano15 || null,
          pgcorte: cliente.pgcorte, // Boolean para string
          dias_corte: cliente.dias_corte || null,
          desbloqueio_confianca: cliente.desbloqueio_confianca || null,
          data_instalacao: cliente.data_instalacao || null,
          data_bloqueio: cliente.data_bloqueio || null,
          data_desbloqueio: cliente.data_desbloqueio || null,
          data_cancelamento: cliente.data_cancelamento || null,
          observacao_contrato: cliente.observacao_contrato || null,
          //ENDEREÇO DO CONTRATO
          endereco_inst: cliente.endereco_inst || cliente.endereco_res,
          numero_inst: cliente.numero_inst || cliente.numero_res,
          bairro_inst: cliente.bairro_inst || cliente.bairro_res,
          cidade_inst: cliente.cidade_inst || cliente.cidade_res,
          estado_inst: cliente.estado_inst || cliente.estado_res,
          cep_inst: cliente.cep_inst || cliente.cep_res,
          complemento_inst: cliente.complemento_inst || cliente.complemento_res,
          coordenadas: cliente.coordenadas || null,
          referencia: cliente.referencia || null,          
          //DADOS FINANCEIRO DO CONTRATO
          dia_vencimento: cliente.dia_vencimento || '',
          isento: cliente.isento, // Boolean para string          
          gateway_id: cliente.gateway_id || null,
          desconto: cliente.desconto || null,
          acrescimo: cliente.acrescimo || null,
          taxa_instalacao: cliente.taxa_instalacao || null,
          modalidade: cliente.modalidade,
          geranfcom: cliente.geranfcom,          
          valid: true,
          duplicate: false,
          validationErrors: []
        };

        // Validações básicas
        let isValid = true;
        let validationErrors: string[] = [];

        if (!clienteData.nome) {
          isValid = false;
          validationErrors.push('Nome obrigatório');
        }

        if (!clienteData.cpf_cnpj) {
          isValid = false;
          validationErrors.push('CPF/CNPJ obrigatório');
        }

        if (!clienteData.endereco_res) {
          isValid = false;
          validationErrors.push('Endereço obrigatório');
        }

        if (!clienteData.bairro_res) {
          isValid = false;
          validationErrors.push('Bairro obrigatório');
        }

        if (!clienteData.cidade_res) {
          isValid = false;
          validationErrors.push('Cidade obrigatória');
        }

        if (!clienteData.estado_res) {
          isValid = false;
          validationErrors.push('Estado obrigatório');
        }

        if (!clienteData.cep_res) {
          isValid = false;
          validationErrors.push('CEP obrigatório');
        }

        if (clienteData.email && !clienteData.email.includes('@')) {
          isValid = false;
          validationErrors.push('Email inválido');
        }

        if (clienteData.data_nascimento && !isValidDateFormat(clienteData.data_nascimento)) {
          isValid = false;
          validationErrors.push('Data de nascimento deve estar no formato yyyy-mm-dd');
        }
        if (clienteData.data_cadastro && !isValidDateFormat(clienteData.data_cadastro)) {
          isValid = false;
          validationErrors.push('Data de cadastro deve estar no formato yyyy-mm-dd');
        }

        clienteData.valid = isValid;
        clienteData.validationErrors = validationErrors;

        data.push(clienteData);
      });

      return data;
    } catch (error) {
      console.error('Erro ao processar dados da API:', error);
      throw new Error('Erro ao processar dados da API');
    }
  };

  const handleApiUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiUrl(event.target.value);
    setError(null);
  };

  const handleApiTypeClientChange = (event: SelectChangeEvent<string>) => {
    setApiTypeClient(event.target.value);
    setError(null);
  };

  const handleApiTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(event.target.value);
    setError(null);
  };

  const handleFetchData = async () => {
    if (!apiUrl.trim()) {
      setError('URL da API é obrigatória');
      return;
    }

    if (!apiToken.trim()) {
      setError('Token da API é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fazer requisição para a API externa
      const response = await fetch(apiUrl + '/' + apiTypeClient, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const apiData = await response.json();
      
      // Verificar se a resposta contém dados de clientes
      let clientsData: any[];
      
      if (Array.isArray(apiData)) {
        clientsData = apiData;
      } else if (apiData && typeof apiData === 'object') {
        // Procurar por propriedades que contêm arrays de clientes
        const possibleArrayKeys = Object.keys(apiData).filter(key => 
          Array.isArray(apiData[key])
        );
        
        if (possibleArrayKeys.length === 0) {
          throw new Error('Nenhum array de clientes encontrado na resposta da API');
        }
        
        // Usar o primeiro array encontrado (geralmente é 'data', 'clients', 'clientes', etc.)
        const arrayKey = possibleArrayKeys[0];
        clientsData = apiData[arrayKey];
        console.log(`Array de clientes encontrado na propriedade: ${arrayKey}`);
      } else {
        throw new Error('Formato de resposta inválido da API');
      }

      if (!Array.isArray(clientsData) || clientsData.length === 0) {
        throw new Error('Nenhum cliente encontrado na resposta da API');
      }
      
      // Processar dados da API
      const parsedData = parseApiData(clientsData);
      
      // Detectar duplicados (baseado no CPF/CNPJ)
      const duplicateCpfCnpj = new Set();
      const finalData = parsedData.map(item => {
        const cpfCnpj = item.cpf_cnpj?.trim();
        if (cpfCnpj && duplicateCpfCnpj.has(cpfCnpj)) {
          return { ...item, duplicate: true };
        }
        if (cpfCnpj) {
          duplicateCpfCnpj.add(cpfCnpj);
        }
        return item;
      });

             // Ordenar dados: primeiro inválidos, depois válidos, por último duplicados
       const sortedData = finalData.sort((a, b) => {
         // Primeiro critério: inválidos primeiro
         if (!a.valid && b.valid) return -1;
         if (a.valid && !b.valid) return 1;
         
         // Segundo critério: válidos não-duplicados antes de duplicados
         if (a.valid && !a.duplicate && b.duplicate) return -1;
         if (a.duplicate && b.valid && !b.duplicate) return 1;
         
         // Terceiro critério: ordenar por ID (numérico se possível)
         const idA = parseInt(a.id) || 0;
         const idB = parseInt(b.id) || 0;
         return idA - idB;
       });

      setParsedData(sortedData);
      setImportStats({
        total: finalData.length,
        valid: finalData.filter(item => item.valid && !item.duplicate).length,
        invalid: finalData.filter(item => !item.valid).length,
        duplicates: finalData.filter(item => item.duplicate).length
      });
      setSelectedRows(new Set()); // Limpar seleção ao buscar novos dados
      setActiveStep(1);
      toast.success(`Dados obtidos com sucesso! ${finalData.length} registros encontrados.`);
    } catch (err: any) {
      console.error('Erro ao buscar dados da API:', err);
      setError(err.message || 'Erro ao conectar com a API. Verifique a URL e o token.');
    } finally {
      setLoading(false);
    }
  };

  // Funções de edição removidas - interface simplificada

  const handleStartImport = async () => {
    // Validar escritório
    if (!escritorioId) {
      setError('Selecione um escritório antes de importar');
      toast.error('Selecione um escritório antes de importar');
      return;
    }

    setImporting(true);
    setImportProgress({});
    setImportCompleted(false);
    
    try {
      // Filtrar apenas os registros SELECIONADOS e válidos
      const selectedIndices = Array.from(selectedRows);
      const selectedData = parsedData.filter((item, index) => 
        selectedIndices.includes(index) && item.valid
      );
      
      if (selectedData.length === 0) {
        throw new Error('Nenhum cliente selecionado ou válido para importar');
      }

      // Marcar todos os registros selecionados como "processando"
      const initialProgress: { [key: number]: ImportProgress } = {};
      selectedData.forEach(cliente => {
        const originalIndex = parsedData.findIndex(p => p === cliente);
        initialProgress[originalIndex] = {
          nome: cliente.nome,
          status: 'processando'
        };
      });
      setImportProgress(initialProgress);

      console.log(`🚀 Iniciando importação em lote de ${selectedData.length} clientes selecionados...`);

      // Usar o serviço de importação
      const result = await importService.importClients({
        tableData: selectedData, // Apenas os registros SELECIONADOS
        escritorio_id: escritorioId // ID do escritório selecionado
      });

      console.log('📊 Resultado da importação em lote:', result);

      if (result.success) {
        const { summary, results } = result.data;
        
        // Não definir importProgress aqui - será definido após a remoção dos sucessos
        setImportCompleted(true);
        
        // Mostrar resultado final baseado no sumário
        if (summary.success > 0) {
          toast.success(`${summary.success} clientes importados com sucesso!`);
        }
        
        if (summary.errors > 0) {
          toast.warning(`${summary.errors} clientes não puderam ser importados. Verifique os detalhes na tabela.`);
        }

        // Se todos foram importados com sucesso, chamar onSuccess
        if (summary.errors === 0) {
          setTimeout(() => onSuccess(), 2000);
        }
        
        // Remover registros importados com sucesso da tabela e da seleção
        // IMPORTANTE: results está na mesma ordem de selectedData, então results[i] corresponde a selectedData[i]
        const successfulIndices: number[] = [];
        const errorIndices: number[] = [];
        
        // Mapear todos os resultados para seus índices no parsedData
        // Criar um mapa para armazenar o resultado e cliente de cada índice
        const resultMap: Map<number, { result: any; cliente: any }> = new Map();
        
        results.forEach((result: any, originalIndex: number) => {
          const cliente = selectedData[originalIndex];
          const parsedDataIndex = parsedData.findIndex(p => p === cliente);
          
          if (parsedDataIndex !== -1) {
            resultMap.set(parsedDataIndex, { result, cliente });
            
            if (result.status === 'sucesso') {
              successfulIndices.push(parsedDataIndex);
            } else {
              errorIndices.push(parsedDataIndex);
            }
          }
        });
        
        // Remover registros importados com sucesso e recalcular importProgress
        if (successfulIndices.length > 0) {
          // Remover registros importados com sucesso
          const remainingData = parsedData.filter((_, index: number) => !successfulIndices.includes(index));
          
          // Recalcular índices de selectedRows após a remoção
          const sortedSuccessfulIndices = [...successfulIndices].sort((a, b) => a - b);
          const newSelectedRows = new Set<number>();
          
          selectedRows.forEach((oldIndex: number) => {
            if (!successfulIndices.includes(oldIndex)) {
              const removedBefore = sortedSuccessfulIndices.filter(idx => idx < oldIndex).length;
              const newIndex = oldIndex - removedBefore;
              newSelectedRows.add(newIndex);
            }
          });
          
          // Recalcular importProgress com os novos índices após a remoção
          const recalculatedProgress: { [key: number]: ImportProgress } = {};
          errorIndices.forEach((oldErrorIndex: number) => {
            const mapped = resultMap.get(oldErrorIndex);
            if (mapped && mapped.result.status === 'erro') {
              // Calcular o novo índice após a remoção
              const removedBefore = sortedSuccessfulIndices.filter(idx => idx < oldErrorIndex).length;
              const newIndex = oldErrorIndex - removedBefore;
              
              recalculatedProgress[newIndex] = {
                nome: mapped.cliente.nome,
                status: 'erro',
                erro: mapped.result.erro || mapped.result.message || 'Erro desconhecido na importação'
              };
            }
          });
          
          setParsedData(remainingData);
          setSelectedRows(newSelectedRows);
          setImportProgress(recalculatedProgress);
          
          console.log(`🗑️ Removidos ${successfulIndices.length} registros importados com sucesso da tabela`);
          console.log(`⚠️ Mantidos ${errorIndices.length} registros com erro na tabela`);
          console.log('📊 ImportProgress recalculado:', recalculatedProgress);
          toast.info(`${successfulIndices.length} registros importados foram removidos da tabela`);
        } else {
          // Se não há sucessos, apenas atualizar o importProgress com os erros (sem remoção)
          const errorProgress: { [key: number]: ImportProgress } = {};
          results.forEach((result: any, originalIndex: number) => {
            if (result.status === 'erro') {
              const cliente = selectedData[originalIndex];
              const originalIndexInParsed = parsedData.findIndex(p => p === cliente);
              
              if (originalIndexInParsed !== -1) {
                errorProgress[originalIndexInParsed] = {
                  nome: cliente.nome,
                  status: 'erro',
                  erro: result.erro || result.message || 'Erro desconhecido na importação'
                };
              }
            }
          });
          
          setImportProgress(errorProgress);
          console.log(`⚠️ Nenhum registro foi removido. ${errorIndices.length} registros com erro mantidos na tabela`);
          console.log('📊 ImportProgress (sem remoção):', errorProgress);
        }
        
      } else {
        throw new Error(result.message || 'Erro na importação em lote');
      }
      
    } catch (err: any) {
      console.error('❌ Erro ao importar clientes em lote:', err);
      
      // Marcar todos os selecionados como erro se a requisição falhou completamente
      const errorProgress: { [key: number]: ImportProgress } = {};
      const selectedIndices = Array.from(selectedRows);
      const selectedData = parsedData.filter((item, index) => 
        selectedIndices.includes(index) && item.valid
      );
      
      selectedData.forEach(cliente => {
        const originalIndex = parsedData.findIndex(p => p === cliente);
        errorProgress[originalIndex] = {
          nome: cliente.nome,
          status: 'erro',
          erro: err.message || 'Erro na importação'
        };
      });
      
      setImportProgress(errorProgress);
      setError(err.message || 'Erro ao importar clientes. Tente novamente.');
      toast.error(err.message || 'Erro ao importar clientes');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setApiUrl('');
    setApiToken('');
    setEscritorioId(null);
    setParsedData([]);
    setError(null);
    setLoading(false);
    // setEditingData removido
    setImporting(false);
    setImportProgress({});
    setImportCompleted(false);
    setSelectedRows(new Set());
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API externa para importar dados de clientes
      </Typography>
      
      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CloudUpload sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Configuração da API
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Informe a URL e token de acesso da API externa
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                label="URL da API"
                placeholder="https://api.exemplo.com/clientes"
                value={apiUrl}
                onChange={handleApiUrlChange}
                fullWidth
                variant="outlined"
                helperText="URL completa da API que retorna dados de clientes"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                      <CloudUpload color="action" />
                    </Box>
                  )
                }}
              />
              <FormControl variant="outlined" sx={{ minWidth: 300 }}>
                <InputLabel>Tipo de Cliente</InputLabel>
                <Select
                  value={apiTypeClient}
                  onChange={handleApiTypeClientChange}
                  label="Tipo de Cliente"
                >
                  <MenuItem value="listarClientesAtivos">Clientes Ativos</MenuItem>
                  <MenuItem value="listarClientesCancelados">Clientes Cancelados (1 ano)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TextField
              label="Token de Acesso"
              placeholder="seu_token_de_acesso_aqui"
              value={apiToken}
              onChange={handleApiTokenChange}
              fullWidth
              variant="outlined"
              type="password"
              helperText="Token Bearer para autenticação na API"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Description color="action" />
                  </Box>
                )
              }}
            />

            {apiUrl && apiToken && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: 'success.light', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" color="success.dark">
                  ✓ Configuração da API preenchida. Clique em "Buscar Dados" para continuar.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderAnalysisStep = () => (
    <Box>
      
      
      {/* Seleção de Escritório */}
      <Box sx={{ mb: 3 }}>
        <EscritorioSelect
          value={escritorioId}
          onChange={(value) => {
            setEscritorioId(value as number | null);
            setError(null);
          }}
          label="Escritório"
          required
          helperText="Selecione o escritório onde os clientes serão importados"
          size="medium"
          error={error === 'Selecione um escritório antes de importar'}
        />
      </Box>

      {/* Barra de progresso durante importação */}
      {importing && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Importando clientes...
          </Typography>
        </Box>
      )}

      {/* Estatísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {importStats.total}
              </Typography>
              <Typography variant="caption">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {importStats.valid}
              </Typography>
              <Typography variant="caption">
                Válidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {importStats.invalid}
              </Typography>
              <Typography variant="caption">
                Inválidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {importStats.duplicates}
              </Typography>
              <Typography variant="caption">
                Duplicados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contador de seleção */}
      {selectedRows.size > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Alert severity="info" sx={{ flex: 1 }}>
            <strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'registro selecionado' : 'registros selecionados'}
          </Alert>
          <Button 
            onClick={() => setSelectedRows(new Set())} 
            size="small" 
            sx={{ ml: 2 }}
            variant="outlined"
          >
            Limpar Seleção
          </Button>
        </Box>
      )}

      {/* DataGrid com paginação e checkbox customizado */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={parsedData.map((row, index) => ({ ...row, rowIndex: index }))}
          getRowId={(row) => row.rowIndex}
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <IconButton
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.size === 0 ? (
                    <CheckBoxOutlineBlank fontSize="small" />
                  ) : selectedRows.size === parsedData.length ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <IndeterminateCheckBox fontSize="small" color="primary" />
                  )}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton
                  size="small"
                  onClick={() => handleSelectRow(params.row.rowIndex)}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.has(params.row.rowIndex) ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 150,
              renderCell: (params) => {
                const row = params.row;
                const index = params.row.rowIndex;
                const progress = importProgress[index];
                
                // Se está importando ou há progresso, mostrar status de importação
                if (importing || progress) {
                  if (progress?.status === 'processando') {
                    return (
                      <Chip
                        icon={<Sync className="animate-spin" />}
                        label="Importando..."
                        color="info"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'sucesso') {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Importado"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'erro') {
                    return (
                      <Chip
                        icon={<ErrorIcon />}
                        label="Erro"
                        color="error"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                        title={progress.erro}
                      />
                    );
                  } else if (importing && row.valid) {
                    return (
                      <Chip
                        icon={<Schedule />}
                        label="Aguardando"
                        color="default"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                }
                
                // Status padrão de validação
                if (row.valid) {
                  if (row.duplicate) {
                    return (
                      <Chip
                        icon={<Warning />}
                        label="Duplicado"
                        color="warning"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Válido"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                } else {
                  return (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Inválido"
                      color="error"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                }
              }
            },
            {
              field: 'erro',
              headerName: 'Erro',
              width: 250,
              renderCell: (params) => {
                const index = params.row.rowIndex;
                const progress = importProgress[index];
                const validationErrors = params.row.validationErrors;
                
                if (progress?.erro) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {progress.erro}
                    </Typography>
                  );
                } else if (validationErrors && validationErrors.length > 0) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {validationErrors.join(', ')}
                    </Typography>
                  );
                }
                return '-';
              }
            },
            ...(parsedData.length > 0 
              ? Object.keys(parsedData[0])
                  .filter(key => !['valid', 'duplicate', 'validationErrors', 'isEditing', 'rowIndex'].includes(key))
                  .map(key => ({
                    field: key,
                    headerName: key.replace(/_/g, ' ').toUpperCase(),
                    width: 150,
                    renderCell: (params: any) => {
                      const row = params.row;
                      const fieldErrors = getFieldValidationErrors(row);
                      const hasError = fieldErrors[key];
                      
                      return (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            border: hasError ? '1px dashed #f44336' : 'none',
                            backgroundColor: hasError ? '#ffebee' : 'transparent',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {String(params.value || '')}
                          </Typography>
                        </Box>
                      );
                    }
                  }))
              : []
            )
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            '& .MuiDataGrid-cell': {
              padding: '4px 8px',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderAnalysisStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={!apiUrl || !apiToken || loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Buscando Dados...' : 'Buscar Dados'}
          </Button>
        );
      case 1:
        const selectedValidCount = Array.from(selectedRows).filter(index => 
          parsedData[index]?.valid
        ).length;
        
        return (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={importing}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={selectedValidCount === 0 || importing}
              color="success"
              startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
            >
              {importing 
                ? `Importando ${selectedValidCount} clientes selecionados...` 
                : importCompleted 
                  ? 'Importação Concluída' 
                  : selectedRows.size === 0
                    ? 'Selecione clientes para importar'
                    : `Importar ${selectedValidCount} cliente${selectedValidCount !== 1 ? 's' : ''} selecionado${selectedValidCount !== 1 ? 's' : ''}`
              }
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Clientes MKAuth
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {getStepContent()}
        
        {/* CSS para animação */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportClientesModal;